import React, { useState, useContext } from 'react';
import { LoginOutlined, UserOutlined, LockOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, message } from 'antd';
import { AppContext, createAppUser } from '../../contexts/AppContext';
import './styles.less';
import { AppName, authBackgroundImg } from '@/config';
import { useTranslation } from 'react-i18next';
import authService from '@/services/authService';
import moment from 'moment';
import { useHistory } from 'react-router';
import { getUrlQueryValue } from '@/hooks/useQuery';
import { Link } from 'react-router-dom';
import LanguageSelector from '@/components/app/LanguageSelector';
import TowFactorAuth from './TowFactorAuth';
import { LoginResponseModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { applyUserLanguage } from '@/helpers/i18n';
import { applyCompanyDateFormat } from '@/config';

export const authLogo = require('@/assets/logos/applogo.png');

const Login = () => {
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();
  const callback = getUrlQueryValue('callback');
  const [loginReponse, setLoginReponse] = React.useState<LoginResponseModel>();
  const setAuthToken = (response: LoginResponseModel) => {
    if (response && response.success && response.currentUser) {
      const { currentUser, refreshToken, isPasswordExpired } = response;
      const expiredTime = moment(new Date()).add(response.expiryDuration, 'minutes');
      authService.saveLoginInfo({
        token: response.accessToken || '',
        expiredTime,
        userId: response.currentUser.id || '',
      });
      authService.logUserInfo2Sentry(currentUser.id, currentUser.email, currentUser.username);
      authService.saveRefreshToken(refreshToken || '');
      applyUserLanguage(currentUser.language);
      applyCompanyDateFormat(currentUser.company?.dateFormat);
      context.onUpdateContext({
        auth: createAppUser({
          ...currentUser,
          isPasswordExpired: isPasswordExpired,
        } as any),
        authLoaded: true,
      });
      if (isPasswordExpired) {
        history.push('/cms/password-expired');
      } else {
        history.push(callback || '/cms');
      }
    } else {
      if (response.error) {
        setError(t(response.error));
      }
    }
  };

  const login = (values: any) => {
    setLoading(true);
    setError('');
    apiSdk.AuthService.login({ body: values })
      .then(response => {
        if (response.twoFactorRequired) {
          setLoginReponse(response);
        } else {
          setAuthToken(response);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div
      className="container-login-form"
      style={{ minHeight: '100vh', backgroundImage: `url(${authBackgroundImg})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}
    >
      <Card className="login-form" bodyStyle={{ padding: 0, border: 'none' }} style={{ border: 'none' }}>
        <div
          style={{
            textAlign: 'center',
            marginBottom: 10,
            paddingTop: 20,
            paddingBottom: 20,
            position: 'relative',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: '#dddddd80',
          }}
        >
          <img src={authLogo} alt="auth-logo" style={{ width: 100 }} />
          <span style={{ color: '#0a8ca3', fontSize: 50, marginLeft: 10, textTransform: 'uppercase', fontWeight: 'bold' }}>{AppName}</span>

          <div className="change-language-container">
            <LanguageSelector />
          </div>
        </div>
        <div style={{ padding: 10, paddingBottom: 20 }}>
          {error ? (
            <Alert
              message={error}
              type="warning"
              closable
              style={{ marginBottom: 10 }}
              onClose={() => {
                setError('');
              }}
            />
          ) : null}
          {loginReponse?.twoFactorRequired ? (
            <TowFactorAuth loginResponse={loginReponse} on2FaVerifySuccess={setAuthToken} />
          ) : (
            <Form
              layout="vertical"
              name="normal_login"
              size="large"
              initialValues={{ remember: true }}
              onFinish={values => {
                login(values);
              }}
            >
              <Form.Item label={t('UserNameOrEmail')} name="email" rules={[{ required: true, message: t('UserNameValidation') }]}>
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder={t('UserNameOrEmail')}
                  style={{ alignItems: 'center' }}
                />
              </Form.Item>
              <Form.Item label={t('Password')} name="password" rules={[{ required: true, message: t('PasswordValidation') }]}>
                <Input.Password
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  placeholder={t('Password')}
                  style={{ alignItems: 'center' }}
                  className="center"
                />
              </Form.Item>
              <div style={{ textAlign: 'end', marginBottom: 20 }}>
                <Link to={'/cms/forgot-password'} style={{ color: '#5F63F2' }}>
                  {t('ForgotPassword')}
                </Link>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button loading={loading} type="primary" htmlType="submit" className="login-form-button" icon={<LoginOutlined />}>
                  {t('LoginButton')}
                </Button>
              </div>
            </Form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Login;
